import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import { HoverCard } from "radix-ui";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { APP_LOCALE, moneyFormatter } from "@/config/currency";
import { conversionRate, ruleChanged, type BotFunnel, type BotRule, type Totals } from "@/lib/bot-funnel";
import { cn } from "@/lib/utils";

const int = new Intl.NumberFormat(APP_LOCALE);
const moneyShort = moneyFormatter({ maximumFractionDigits: 0 });
const pct = (part: number, whole: number) =>
  whole > 0 ? (part > 0 && part / whole < 0.001 ? "<0.1%" : `${((part / whole) * 100).toFixed(1)}%`) : "—";

/** Columns of every staircase row from lg up: rule, bar, cut, left, regs, sales, revenue. */
const COLS = "lg:grid-cols-[14rem_minmax(8rem,1fr)_8.5rem_8rem_3.5rem_3.5rem_5rem]";
/** Bars move 400 ms, each row 50 ms after the one above (--d), none with reduced motion. */
const MOVE =
  "transition-[left,width,opacity] duration-[400ms] ease-[cubic-bezier(.22,1,.36,1)] [transition-delay:var(--d)] motion-reduce:transition-none";

/** A number that counts to its new value (500 ms, ease-out) instead of jumping; instant with reduced motion. */
function Num({ value, format }: { value: number; format: (n: number) => string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    const instant = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t0 = performance.now();
    let id = requestAnimationFrame(function tick(t) {
      const p = instant ? 1 : Math.min(1, (t - t0) / 500);
      from.current = start + (value - start) * (1 - (1 - p) ** 3);
      setShown(from.current);
      if (p < 1) id = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(id);
  }, [value]);
  return <>{format(Math.round(shown))}</>;
}

const minus = (n: number) => (n ? `−${int.format(n)}` : "—");
const minusMoney = (n: number) => (n ? `−${moneyShort.format(n)}` : "—");

/** A before → after card: what is left after the filters, out of what came in. */
function KpiCard({ title, field, before, after, format, lossIsBad, note }: {
  title: string;
  /** Which Keitaro numbers these are, shown in brackets. */
  field?: string;
  before: number;
  after: number;
  format: (n: number) => string;
  /** Losing these is a cost (sales, revenue), not the point (bot clicks). */
  lossIsBad: boolean;
  note?: string;
}) {
  const lost = before - after;
  const share = before > 0 ? after / before : 1;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs font-medium tracking-wide text-muted-foreground">
        <span className="uppercase">{title}</span>
        {field ? <span className="font-normal normal-case"> ({field})</span> : null}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">{format(after)}</span>
        <span className="text-sm text-muted-foreground tabular-nums">of {format(before)}</span>
      </div>
      <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-chart-1" style={{ width: `${share * 100}%` }} />
        {lossIsBad && lost > 0 ? (
          <div className="h-full min-w-0.5 bg-signal-negative-foreground" style={{ width: `${(1 - share) * 100}%` }} />
        ) : null}
      </div>
      <div
        className={cn(
          "mt-2 text-xs tabular-nums",
          !lossIsBad ? "text-muted-foreground" : lost > 0 ? "signal-text-negative" : "signal-text-positive",
        )}
      >
        {!lossIsBad
          ? `−${format(lost)} filtered (${pct(lost, before)})`
          : lost > 0
            ? `−${format(lost)} cut by the filters (${pct(lost, before)})`
            : "nothing cut by the filters"}
      </div>
      {note ? <div className="mt-1 text-xs text-muted-foreground">{note}</div> : null}
    </div>
  );
}

/** Solid = left, hatched = cut at this step, dashed = would cut; all as shares of every click. */
function Bar({ keep, cut = 0, would = 0, tone, delay }: {
  keep: number;
  cut?: number;
  would?: number;
  tone: "trunk" | "off" | "clean";
  delay: number;
}) {
  const w = (share: number) => (share > 0 ? `max(2px, ${share * 100}%)` : "0");
  return (
    <div className="relative h-6" style={{ "--d": `${delay}ms` } as CSSProperties}>
      <div
        className={cn(
          "absolute inset-y-0 left-0 rounded",
          tone === "clean" ? "bg-chart-1" : tone === "off" ? "bg-muted-foreground/20" : "bg-muted-foreground/55",
          MOVE,
        )}
        style={{ width: `${keep * 100}%` }}
      />
      <div
        className={cn("bot-cut absolute inset-y-0 rounded-r", MOVE)}
        style={{ left: `${keep * 100}%`, width: w(cut), opacity: cut > 0 ? 1 : 0 }}
      />
      <div
        className={cn("bot-would absolute inset-y-0 rounded-r", MOVE)}
        style={{ left: `${keep * 100}%`, width: w(would), opacity: would > 0 ? 1 : 0 }}
      />
    </div>
  );
}

/**
 * One staircase row. From lg up: seven columns; below it the rule and its cut
 * on one line, the bar under them, then the rest in a line of text.
 */
function Row({ name, bar, cut, left, totals, signed, muted, mobileLeft, flashKey }: {
  name: ReactNode;
  bar: ReactNode;
  cut: ReactNode;
  left: ReactNode;
  totals: Totals;
  /** The row's registrations / sales / revenue are what it removes (−) rather than what is there. */
  signed: boolean;
  muted?: boolean;
  mobileLeft: string;
  flashKey?: number;
}) {
  const costs = signed && (totals.sales > 0 || totals.revenue > 0);
  const regs = signed ? minus(totals.conversions) : int.format(totals.conversions);
  const sales = signed ? minus(totals.sales) : int.format(totals.sales);
  const money = signed ? minusMoney(totals.revenue) : moneyShort.format(totals.revenue);
  const moneyTone = cn(costs ? "signal-text-negative" : muted && "text-muted-foreground", muted && costs && "opacity-70");
  return (
    <div className={cn("relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-2 lg:gap-y-0", COLS)}>
      {flashKey !== undefined ? (
        <span key={flashKey} className="bot-flash pointer-events-none absolute inset-0 -mx-2 rounded-md" />
      ) : null}
      <div className="order-1 flex min-w-0 items-center gap-2 lg:order-none">{name}</div>
      <div className="order-3 col-span-2 lg:order-none lg:col-span-1">{bar}</div>
      <div className={cn("order-2 text-right text-sm tabular-nums lg:order-none", muted && "text-muted-foreground")}>{cut}</div>
      <div className={cn("hidden text-right text-sm tabular-nums lg:block", muted && "text-muted-foreground")}>{left}</div>
      <div className={cn("hidden text-right text-sm tabular-nums lg:block", muted && "text-muted-foreground")}>{regs}</div>
      <div className={cn("hidden text-right text-sm tabular-nums lg:block", moneyTone)}>{sales}</div>
      <div className={cn("hidden text-right text-sm tabular-nums lg:block", moneyTone)}>{money}</div>
      <div className="order-4 col-span-2 text-xs text-muted-foreground tabular-nums lg:hidden">
        {mobileLeft} · {regs} regs · <span className={moneyTone}>{sales} sales · {money}</span>
      </div>
    </div>
  );
}

export interface BotFunnelViewProps {
  funnel: BotFunnel;
  /** The saved rules: a row that differs from them gets a dot. */
  saved: BotRule[];
  /** The rule a switch changed last: its row flashes, the rows below follow it. */
  lastToggle: { key: string; seq: number } | null;
  onToggle: (rule: BotRule, enabled: boolean) => void;
  periodLabel: string;
  /** A checkbox per rule where nothing changes until Apply (Clicks → Bots); a switch in Settings. */
  control?: "switch" | "checkbox";
  /** What a rule matches, shown on hovering or focusing its name. */
  ruleInfo?: (rule: BotRule) => ReactNode;
  /** What the dot by a rule means here. */
  draftLabel?: string;
}

/**
 * Settings → Bot Detection: the period's clicks as a staircase. Every rule is a
 * row with its switch, in the order the rules apply (a click is cut by the
 * first one that matches it), so switching one moves the funnel right where
 * you click and nothing else on the page shifts. Descriptions and lists are
 * edited in the rules section below the funnel (BotDetectionSettings).
 */
export function BotFunnelView({
  funnel,
  saved,
  lastToggle,
  onToggle,
  periodLabel,
  control = "switch",
  ruleInfo,
  draftLabel = "changed, not saved yet",
}: BotFunnelViewProps) {
  const { total, clean, stages } = funnel;
  const crBefore = conversionRate(total);
  const crAfter = conversionRate(clean);
  const crNote =
    crBefore === null || crAfter === null
      ? undefined
      : `CR (registrations / unique_global) ${crBefore.toFixed(2)}% → ${crAfter.toFixed(2)}%`;
  const share = (t: Totals) => (total.clicks > 0 ? t.clicks / total.clicks : 0);
  const changedAt = lastToggle ? stages.findIndex((s) => s.rule.key === lastToggle.key) : -1;
  const delay = (i: number) => (changedAt < 0 ? 0 : Math.max(0, i - changedAt) * 50);
  const isDraft = (rule: BotRule) => ruleChanged(rule, saved.find((x) => x.key === rule.key));

  const ruleName = (rule: BotRule) => {
    const label = <span className={cn("min-w-0 text-sm", !rule.enabled && "text-muted-foreground")}>{rule.label}</span>;
    const dot = isDraft(rule) ? <span className="size-1.5 shrink-0 rounded-full bg-chart-1" title={draftLabel} /> : null;
    const name =
      control === "checkbox" ? (
        <label className="flex min-w-0 cursor-pointer items-center gap-2">
          <Checkbox checked={rule.enabled} onCheckedChange={(v) => onToggle(rule, v === true)} aria-label={rule.label} />
          {label}
          {dot}
        </label>
      ) : (
        <>
          <Switch checked={rule.enabled} onCheckedChange={(v) => onToggle(rule, v)} aria-label={rule.label} />
          {label}
          {dot}
        </>
      );
    if (!ruleInfo) return name;
    return (
      <HoverCard.Root openDelay={250} closeDelay={150}>
        <HoverCard.Trigger asChild>
          <span className="flex min-w-0 items-center gap-2">{name}</span>
        </HoverCard.Trigger>
        <HoverCard.Portal>
          <HoverCard.Content
            // Left: over the presets, not over the numbers the rule moves.
            side="left"
            align="start"
            sideOffset={12}
            collisionPadding={12}
            className="z-50 w-80 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            {ruleInfo(rule)}
          </HoverCard.Content>
        </HoverCard.Portal>
      </HoverCard.Root>
    );
  };
  return (
    <div className="space-y-4">
      {funnel.legacy.clicks > 0 ? (
        <div className="signal-card-warning flex gap-3 rounded-lg border p-3 text-sm">
          <TriangleAlert className="signal-text-warning mt-0.5 size-4 shrink-0" />
          <div className="space-y-1">
            <p className="signal-text-warning font-medium">
              {int.format(funnel.legacy.clicks)} of {int.format(total.clicks)} clicks in this period (
              {pct(funnel.legacy.clicks, total.clicks)}) have no bot data yet
            </p>
            <p className="text-xs text-muted-foreground">
              Their days were last refreshed before these rules existed, so no rule sees anything there: those
              clicks count as not classified and the rules' numbers are too low (marked ≥). The hourly refresh
              fills in fresh days; older ones need a recompute.
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Clicks" field="all clicks, not unique" before={total.clicks} after={clean.clicks} format={int.format} lossIsBad={false} />
        <KpiCard title="Conversions" field="registrations" before={total.conversions} after={clean.conversions} format={int.format} lossIsBad note={crNote} />
        <KpiCard title="Sales" before={total.sales} after={clean.sales} format={int.format} lossIsBad />
        <KpiCard title="Revenue" before={total.revenue} after={clean.revenue} format={moneyShort.format} lossIsBad />
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-semibold">Funnel</h3>
          <span className="text-xs text-muted-foreground">{periodLabel}</span>
        </div>
        <p className="mt-1 mb-3 text-xs text-muted-foreground">
          Clicks are all Keitaro clicks, not unique. Rules apply top to bottom: a click is cut by the first rule
          that matches it. Solid: left · hatched: cut at this step · dashed: would cut if on ·{" "}
          <span className="inline-block size-1.5 rounded-full bg-chart-1 align-middle" /> {draftLabel}.
        </p>

        <div className={cn("hidden border-b border-border pb-1.5 text-xs text-muted-foreground lg:grid lg:gap-x-3", COLS)}>
          <span>Rule</span>
          <span />
          <span className="text-right">Cut at this step</span>
          <span className="text-right">Left after</span>
          <span className="text-right">Regs</span>
          <span className="text-right">Sales</span>
          <span className="text-right">Revenue</span>
        </div>

        <div className="divide-y divide-border/60">
          <Row
            name={<span className="text-sm font-medium">All clicks</span>}
            bar={<Bar keep={1} tone="trunk" delay={0} />}
            cut={<span className="lg:hidden"><Num value={total.clicks} format={int.format} /></span>}
            left={<Num value={total.clicks} format={int.format} />}
            totals={total}
            signed={false}
            mobileLeft="100%"
          />

          {stages.map((s, i) => {
            const on = s.rule.enabled;
            // Days without bits: no rule sees them, so every rule counts too few there.
            const atLeast = funnel.legacy.clicks > 0 ? (
              <span className="signal-text-warning" title="Not computed on every day yet: at least this much">≥</span>
            ) : null;
            return (
              <Row
                key={s.rule.key}
                name={ruleName(s.rule)}
                bar={
                  on ? (
                    <Bar keep={share(s.after)} cut={share(s.cut)} tone="trunk" delay={delay(i)} />
                  ) : (
                    <Bar keep={share(s.before) - share(s.only)} would={share(s.only)} tone="off" delay={delay(i)} />
                  )
                }
                cut={
                  on ? (
                    <>
                      <span className="font-medium">
                        {atLeast}
                        <Num value={s.cut.clicks} format={(v) => `−${int.format(v)}`} />
                      </span>{" "}
                      <span className="text-xs text-muted-foreground">{pct(s.cut.clicks, total.clicks)}</span>
                      {/* Always two lines, so switching never changes the row's height. */}
                      <span
                        className={cn("block text-[11px] text-muted-foreground", s.only.clicks === s.cut.clicks && "invisible")}
                        aria-hidden={s.only.clicks === s.cut.clicks}
                      >
                        only this rule <Num value={s.only.clicks} format={int.format} />
                      </span>
                    </>
                  ) : (
                    <>
                      would {atLeast}
                      <Num value={s.only.clicks} format={(v) => `−${int.format(v)}`} />{" "}
                      <span className="text-xs">{pct(s.only.clicks, total.clicks)}</span>
                      <span className="invisible block text-[11px]" aria-hidden>
                        only this rule
                      </span>
                    </>
                  )
                }
                left={
                  <>
                    <Num value={s.after.clicks} format={int.format} />{" "}
                    <span className="text-xs text-muted-foreground">{pct(s.after.clicks, total.clicks)}</span>
                  </>
                }
                totals={on ? s.cut : s.only}
                signed
                muted={!on}
                mobileLeft={`left ${int.format(s.after.clicks)} (${pct(s.after.clicks, total.clicks)})`}
                flashKey={lastToggle?.key === s.rule.key ? lastToggle.seq : undefined}
              />
            );
          })}

          <Row
            name={<span className="text-sm font-semibold">Clean traffic</span>}
            bar={<Bar keep={share(clean)} tone="clean" delay={delay(stages.length)} />}
            cut={
              <span className="lg:hidden font-semibold">
                <Num value={clean.clicks} format={int.format} />
              </span>
            }
            left={
              <span className="font-semibold">
                <Num value={clean.clicks} format={int.format} />{" "}
                <span className="text-xs font-normal text-muted-foreground">{pct(clean.clicks, total.clicks)}</span>
              </span>
            }
            totals={clean}
            signed={false}
            mobileLeft={pct(clean.clicks, total.clicks)}
          />
        </div>

        {funnel.overlap.clicks > 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {int.format(funnel.overlap.clicks)} of the cut clicks are caught by two or more rules that are on: switching
            one of them off gives none of those back
            {funnel.overlap.sales > 0 ? ` (they hold ${funnel.overlap.sales} of the hidden sales)` : ""}.
          </p>
        ) : null}
      </section>
    </div>
  );
}
